import { useState } from 'react';

import { ArrowButton, Button, DatePicker, Modal, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { useValidation } from '@king-ict/eupisi-ui/hooks';
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { createLanguageLevel, editLanguageLevel } from "../../api/nisooApi";
import { useInput } from "../../hooks";
import { CodeBookExtended, CodeBookValidationScheme } from '../../models/CodeBook';
import { codebookRouteLanguageLevel } from '../../routes/routes';
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookLanguageLevelEdit(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { handleChange } = useInput();
  const { t } = useTranslation();

  const getLanguageLevelInitial = () => {
    if ((location.state as any).languageLevel) {
      return (location.state as any).languageLevel as CodeBookExtended;
    }
    return { id:0, name: "", canDelete: true };
  }

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [languageLevel, setLanguageLevel] = useState<CodeBookExtended>(getLanguageLevelInitial());

  const [errors, doValidations] = useValidation({
    data: languageLevel,
    validationSchema: CodeBookValidationScheme,
  });

  const saveChanges = () => {
    var isValid = doValidations();
    if (isValid) {
      setOpenConfirmModal(true);
    }
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    if (languageLevel.dateTo) {
      let date = new Date(languageLevel.dateTo);
      languageLevel.dateTo = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    }

    if (languageLevel.id) {
      await editLanguageLevel(languageLevel.id, languageLevel);
      navigateToDetails();
    }
    else {
      await createLanguageLevel(languageLevel);
      navigateToDetails();
    }
  }

  const cancel = () => {
    if (languageLevel.id) {
      navigateToDetails()
    } else {
      navigateToList();
    }
  }

  const navigateToDetails = () => {
    history.push(
      codebookRouteLanguageLevel(),
      {
        page: "details",
        languageLevel: languageLevel
      }
    );
  }

  const navigateToList = () => {
    history.push(
      codebookRouteLanguageLevel()
    );
  }
  return (
    <>
      <Row>
        <Col md={12}>
          <ArrowButton
            backwards
            ariaLabel=""
            onClick={navigateToList}
          />
          <span className={styles.backButtonCaption}>
            {t("codeBook.navigateToCodeBook")}
          </span>

        </Col>
      </Row>
      <Row className={styles.title}>
        <Col md={6}>
          <h3>{t("codeBook.languageLevel")}</h3>
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-right">
          <Button
            outline
            onClick={() => cancel()}
            className={styles.actionButton}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => saveChanges()}
            className={styles.actionButton}>
            {t("common.save")}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col md={8} className="mt-5">
          <TextField
            name={nameof<typeof languageLevel>("name")}
            label={t("common.name")}
            value={languageLevel.name!}
            onChange={handleChange(setLanguageLevel)}
            errorText={errors?.name}
            readonly
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mt-5">
          <DatePicker
            name={nameof<typeof languageLevel>("dateTo")}
            label={t("common.dateTo")}
            value={languageLevel.dateTo}
            onChange={handleChange(setLanguageLevel)}
            isClearable={true}
            minYear={new Date().getFullYear() - 25}
            maxYear={new Date().getFullYear() + 25}
          />
        </Col>
      </Row>
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.saveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}